import { FC, useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useFormikContext } from 'formik';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { getTimezoneOptions } from '../../../../utils/timezoneUtils';
import type { DailyResetTimezoneFormValues } from '../storeSettingsView.types';

export const DailyResetTimezoneSectionFields: FC = () => {
  const { values, setFieldValue } = useFormikContext<DailyResetTimezoneFormValues>();
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const selectedOption = useMemo(
    () =>
      timezoneOptions.find(
        (option) => option.value === values.resetTimezoneOffset
      ),
    [timezoneOptions, values.resetTimezoneOffset]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="font-sans text-sm font-medium text-base-foreground">
          Daily Reset Timezone
        </span>
        <Tooltip
          content={
            <span className="block w-[338px] text-xs">
              The selected timezone determines when the daily reset occurs.
              Applies to all features that follow a daily cycle (e.g., Daily
              bonus, Reward calendar). Changes apply from the next reset cycle.
            </span>
          }
          side="top"
          align="center"
        >
          <div className="cursor-pointer text-base-mutedForeground">
            <FontAwesomeIcon icon="fa-regular fa-circle-info" />
          </div>
        </Tooltip>
      </div>
      <InputSelectDropdown
        options={timezoneOptions}
        defaultSelected={selectedOption}
        onOptionChange={(value: string) =>
          setFieldValue('resetTimezoneOffset', value)
        }
        searchPlaceholder="Search timezone..."
        noSearchResultsText="No timezone found."
        shouldCapitalizeOptions={false}
        shouldHighlightSelectedOption
        buttonDataTestId="dailyResetTimezoneButton"
        listDataTestId="dailyResetTimezoneList"
      />
      <p className="font-sans text-sm text-base-mutedForeground">
        Changes apply from the next reset cycle and won&apos;t affect the
        current one.
      </p>
    </div>
  );
};
